import { useRouter, usePathname } from 'next/navigation';
import { useCallback } from 'react';
import {
  getFilterTypeKey,
  buildSlugFromFilters,
  generateFilterValue,
  getCurrentFilters
} from '@helpers/navigationHelpers';

export interface UseBrowseCardNavigationProps {
  onSlugChange?: (newSlug: string) => void;
}

export type BrowseCardClickHandler = (filterType: string, filterValue: string) => void;

export const useBrowseCardNavigation = ({ onSlugChange }: UseBrowseCardNavigationProps = {}) => {
  const router = useRouter();
  const pathname = usePathname();

  const getCurrentSlugFilters = useCallback(() => {
    return getCurrentFilters(pathname);
  }, [pathname]);

  const generateNewSlug = useCallback((filterType: string, filterValue: string): string => {
    if (filterValue === '') {
      return '';
    }

    const currentFilters = getCurrentSlugFilters();
    const updatedFilters = { ...currentFilters };

    const filterTypeKey = getFilterTypeKey(filterType);
    if (filterTypeKey) {
      if (filterTypeKey === 'features') {
        updatedFilters[filterTypeKey] = [generateFilterValue(filterType, filterValue)];
      } else {
        updatedFilters[filterTypeKey] = generateFilterValue(filterType, filterValue);
      }
    }

    return buildSlugFromFilters(updatedFilters);
  }, [getCurrentSlugFilters]);

  const handleBrowseCardClick: BrowseCardClickHandler = useCallback((filterType, filterValue) => {

    const newSlug = generateNewSlug(filterType, filterValue);

    if (onSlugChange) {
      onSlugChange(newSlug || '');
    } else {
      router.push(`/collection/${newSlug}`);
    }
  }, [router, onSlugChange, generateNewSlug]);

  const isFilterActive = useCallback((filterType: string, filterValue: string): boolean => {
    const pathParts = pathname?.split('/');
    const isCollectionPage = pathParts && pathParts.length >= 2 && pathParts[1] === 'collection';

    if (!isCollectionPage) {
      return false;
    }

    const currentFilters = getCurrentSlugFilters();
    const value = generateFilterValue(filterType, filterValue);

    switch (filterType) {
      case 'brand':
        return currentFilters.brand === value;
      case 'bodyType':
        return currentFilters.bodyType === value;
      case 'fuelType':
        return currentFilters.fuelType === value;
      case 'transmission':
        return currentFilters.transmission === value;
      case 'seatingCapacity':
        return currentFilters.seatingCapacity === value;
      case 'budget':
        return currentFilters.budget === value;
      default:
        return false;
    }
  }, [getCurrentSlugFilters, pathname]);

  return {
    handleBrowseCardClick,
    getCurrentSlugFilters,
    isFilterActive,
    generateNewSlug
  };
};
